/**
 * Seeded PRNG. Math.random is forbidden in generation and simulation: every
 * random choice in a circuit comes from a stream seeded by the date string.
 */

/** 32-bit string hash (cyrb53-style mixing, truncated to 32 bits). */
export function hashString(str: string): number {
  let h1 = 0xdeadbeef ^ str.length;
  let h2 = 0x41c6ce57 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h1 ^ h2) >>> 0;
}

/** Hex digest of arbitrary data, used to fingerprint tracks and configs. */
export function hashHex(str: string): string {
  const a = hashString(str);
  const b = hashString(str + '\u0001' + a.toString(16));
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  static fromString(seed: string): Rng {
    return new Rng(hashString(seed));
  }

  /** mulberry32: uniform float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }

  /** Integer in [lo, hi] inclusive. */
  int(lo: number, hi: number): number {
    return lo + Math.floor(this.next() * (hi - lo + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }

  /** Roughly normal in [-1, 1] (sum of uniforms). */
  centered(): number {
    return (this.next() + this.next() + this.next()) / 1.5 - 1;
  }

  /** Independent child stream, so adding a draw in one system never shifts another. */
  fork(label: string): Rng {
    return new Rng(hashString(label + ':' + this.state.toString(16)));
  }

  getState(): number {
    return this.state;
  }
}
